import { createProvider } from '@nvision/services/data-provider';
import { queryOptions } from '@tanstack/react-query';

import type { ContractDocumentsParams, ContractHistoryParams, ContractPricingParams } from '../models';
import { CACHE_KEYS } from '../types';
import { logger } from '../utils/logger';
import * as api from './api';
import { normalizeContractDocuments, normalizeContractHistory, normalizeContractPricing } from './normalize';

export const fetchContractDocuments = createProvider(api.fetchContractDocuments)
  .andThen(normalizeContractDocuments)
  .andCatch((error) => {
    if (error instanceof Error && error.name === 'AbortError') {
      logger.warn('Fetch contract documents is aborted');
    } else {
      logger.error(`Fetch contract documents failed: ${error}`);
    }
    throw error;
  });

export const contractDocumentsQueryOptions = (params: ContractDocumentsParams) =>
  queryOptions({
    enabled: Boolean(params.contractKey),
    queryFn: ({ signal }) => fetchContractDocuments(params, { signal }),
    queryKey: CACHE_KEYS.contractDocuments(params),
    // biome-ignore lint/style/noMagicNumbers: 1 hour
    staleTime: 1000 * 60 * 60,
  });
export const fetchContractPricing = createProvider(api.fetchContractPricing)
  .andThen(normalizeContractPricing)
  .andCatch((error) => {
    if (error instanceof Error && error.name === 'AbortError') {
      logger.warn('Fetch contract pricing is aborted');
    } else {
      logger.error(`Fetch contract pricing failed: ${error}`);
    }
    throw error;
  });

export const contractPricingQueryOptions = (params: ContractPricingParams) =>
  queryOptions({
    enabled: Boolean(params.contractKey),
    queryFn: ({ signal }) => fetchContractPricing(params, { signal }),
    queryKey: CACHE_KEYS.contractPricing(params),
    // biome-ignore lint/style/noMagicNumbers: 1 hour
    staleTime: 1000 * 60 * 60,
  });

export const fetchContractHistory = createProvider(api.fetchContractHistory)
  .andThen(normalizeContractHistory)
  .andCatch((error) => {
    if (error instanceof Error && error.name === 'AbortError') {
      logger.warn('Fetch contract history is aborted');
    } else {
      logger.error(`Fetch contract history failed: ${error}`);
    }
    throw error;
  });

export const contractHistoryQueryOptions = (params: ContractHistoryParams) =>
  queryOptions({
    enabled: Boolean(params.contractKey),
    queryFn: ({ signal }) => fetchContractHistory(params, { signal }),
    queryKey: CACHE_KEYS.contractHistory(params),
    // biome-ignore lint/style/noMagicNumbers: 1 hour
    staleTime: 1000 * 60 * 60,
  });
